/** @format */

import { useEffect } from 'react';

export interface SEOConfig {
	title?: string;
	description?: string;
	keywords?: string;
	canonical?: string;

	robots?: string;
	googlebot?: string;

	ogTitle?: string;
	ogDescription?: string;
	ogType?: 'website' | 'article';
	ogUrl?: string;
	ogSiteName?: string;
	ogImage?: string;

	twitterCard?: 'summary' | 'summary_large_image';
	twitterTitle?: string;
	twitterDescription?: string;
	twitterImage?: string;
}

export function useSEO({
	title,
	description,
	keywords,
	canonical,

	robots = 'index, follow',
	googlebot = 'index, follow',

	ogTitle,
	ogDescription,
	ogType = 'website',
	ogUrl,
	ogSiteName,
	ogImage,

	twitterCard = 'summary_large_image',
	twitterTitle,
	twitterDescription,
	twitterImage,
}: SEOConfig): void {
	useEffect(() => {
		if (title) document.title = title;

		setMeta('description', description);
		setMeta('keywords', keywords);
		setMeta('robots', robots);
		setMeta('googlebot', googlebot);

		setProperty('og:title', ogTitle ?? title);
		setProperty('og:description', ogDescription ?? description);
		setProperty('og:type', ogType);
		setProperty('og:url', ogUrl ?? window.location.href);
		setProperty('og:site_name', ogSiteName);
		setProperty('og:image', ogImage);

		setMeta('twitter:card', twitterCard);
		setMeta('twitter:title', twitterTitle ?? title);
		setMeta('twitter:description', twitterDescription ?? description);
		setMeta('twitter:image', twitterImage ?? ogImage);

		setCanonical(canonical);
	}, [
		title,
		description,
		keywords,
		canonical,
		robots,
		googlebot,
		ogTitle,
		ogDescription,
		ogType,
		ogUrl,
		ogSiteName,
		ogImage,
		twitterCard,
		twitterTitle,
		twitterDescription,
		twitterImage,
	]);
}

/* ---------- helpers ---------- */

function setMeta(name: string, content?: string): void {
	if (!content) return;

	let tag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);

	if (!tag) {
		tag = document.createElement('meta');
		tag.setAttribute('name', name);
		document.head.appendChild(tag);
	}

	tag.setAttribute('content', content);
}

function setProperty(property: string, content?: string): void {
	if (!content) return;

	let tag = document.querySelector<HTMLMetaElement>(
		`meta[property="${property}"]`
	);

	if (!tag) {
		tag = document.createElement('meta');
		tag.setAttribute('property', property);
		document.head.appendChild(tag);
	}

	tag.setAttribute('content', content);
}

function setCanonical(url?: string): void {
	if (!url) return;

	let link = document.querySelector<HTMLLinkElement>(`link[rel="canonical"]`);

	if (!link) {
		link = document.createElement('link');
		link.rel = 'canonical';
		document.head.appendChild(link);
	}

	link.href = url;
}
